import React, { PropsWithChildren } from "react";

import BuildIcon from "@mui/icons-material/Build";
import {
  Backdrop,
  Box,
  Dialog,
  DialogContent,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";

import { useInternationalization } from "../internationalization";
import { useMaintenanceModeComponent } from "./maintenance-mode-component.hook.context.lib";
import { MaintenanceModeContext } from "./maintenance-mode.context.lib";

export const MaintenanceModeProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const { isMaintenanceMode, setMaintenanceMode, scheduleRefresh } = useMaintenanceModeComponent();
  const { t } = useInternationalization();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  return (
    <MaintenanceModeContext.Provider
      value={{ isMaintenanceMode, setMaintenanceMode, scheduleRefresh }}
    >
      {/* Blurred Backdrop */}
      <Backdrop
        open={isMaintenanceMode}
        sx={{
          zIndex: (theme) => theme.zIndex.modal - 1,
          backdropFilter: "blur(5px)",
          backgroundColor: "rgba(134, 134, 134, 0.5)",
        }}
      />
      {/* Maintenance Mode Dialog */}
      <Dialog
        open={isMaintenanceMode}
        onClose={(_, reason) => {
          // Prevent closing by clicking outside or pressing escape
          if (reason === "backdropClick" || reason === "escapeKeyDown") {
            return;
          }
        }}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            m: 2,
            overflow: "hidden",
            maxHeight: isMobile ? "calc(100% - 32px)" : "none",
            zIndex: (theme) => theme.zIndex.modal + 2,
          },
        }}
      >
        {/* Header */}
        <Box
          sx={{
            background: `linear-gradient(135deg, ${theme.palette.warning.main} 0%, ${theme.palette.warning.dark} 100%)`,
            px: 3,
            py: 2,
            display: "flex",
            alignItems: "center",
            gap: 1.5,
          }}
        >
          <BuildIcon sx={{ fontSize: 24, color: "#ffffff" }} />
          <Typography
            variant="h6"
            sx={{
              color: "#ffffff",
              fontWeight: 600,
              fontSize: "1rem",
            }}
          >
            {t("maintenance-mode.title")}
          </Typography>
        </Box>

        <DialogContent sx={{ px: 3, py: 2.5, textAlign: "center" }}>
          <Typography
            variant="body1"
            sx={{
              color: "#344767",
              mb: 1.5,
              lineHeight: 1.6,
              fontSize: "0.95rem",
            }}
          >
            {t("maintenance-mode.message")}
          </Typography>
          <Typography
            variant="caption"
            sx={{
              color: "#7b809a",
              fontStyle: "italic",
              display: "block",
              fontSize: "0.8rem",
            }}
          >
            {t("maintenance-mode.refresh-info")}
          </Typography>
        </DialogContent>
      </Dialog>

      {children}
    </MaintenanceModeContext.Provider>
  );
};
